import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./EasiDermaDetails.css";

const getDifficultyLevel = (s) => {
  if (s === 0) return { text: "Clear", color: "#4caf50" };
  if (s <= 1) return { text: "Almost Clear", color: "#8bc34a" };
  if (s <= 7) return { text: "Mild", color: "#cddc39" };
  if (s <= 21) return { text: "Moderate Severity", color: "#ff9800" };
  if (s <= 50) return { text: "Severe", color: "#ff5722" };
  return { text: "Very Severe", color: "#f44336" };
};

const getReason = (s) => {
  if (s === 0) return "Your Eczema is Clear";
  if (s <= 1) return "Your Eczema is Almost Clear";
  if (s <= 7) return "Your Eczema is Mild";
  if (s <= 21) return "Your Eczema is Moderate Severe";
  if (s <= 50) return "Your Eczema is Severe";
  return "Your Eczema is Very Severe";
};

const EasiDermaDetails = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";
  const type = urlParams.get("type") || "";

  const [scoreData, setScoreData] = useState(null);
  const [reportUrl, setReportUrl] = useState("");
  const [logdate, setLogDate] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [nextDate, setNextDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  useEffect(() => {
    lifestyleApi
      .fetchEasiScore()
      .then((res) => {
        const data = res?.data?.data;
        if (data) {
          setScoreData(data.scoreResponse && data.scoreResponse.length ? data.scoreResponse : null);
          setReportUrl(data.reportUrl || "");
          setLogDate(data.logDate || "");
        }
      })
      .catch((err) => console.error("fetchEasiScore:", err))
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(() => {
    if (logdate) {
      const next = new Date(Date.parse(logdate) + 30 * 24 * 60 * 60 * 1000);
      setNextDate(next);
      if (next > new Date()) setShowPopup(true);
    }
  }, [logdate]);

  const formatDate = (d) => {
    if (!d) return "";
    const obj = d instanceof Date ? d : new Date(d);
    return `${obj.getDate()}-${obj.toLocaleString("default", { month: "short" })}-${obj.getFullYear().toString().slice(-2)}`;
  };

  const buildQs = () =>
    `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;

  const latestScore = scoreData ? scoreData[0]?.score : null;
  const difficulty = latestScore != null ? getDifficultyLevel(latestScore) : { text: "", color: "" };
  const reason = latestScore != null ? getReason(latestScore) : "";

  return (
    <>
      <section className="easid-page">
        <div className="easid-container">
          {loading ? (
            <div className="easid-loading">{t("common.loading", "Loading...")}</div>
          ) : (
            <>
              <p className="easid-description">
                {!type
                  ? t("easiDetails.description", "Eczema Area and Severity Index (EASI) is a questionnaire to evaluate the severity of eczema.")
                  : reason}
              </p>

              {!type && (
                <div className="easid-take-btn-wrap">
                  {showPopup ? (
                    <div className="easid-completed-box">
                      {t("easiDetails.completedOn", "You have completed an EASI on")}{" "}
                      <strong>{formatDate(logdate)}</strong>.{" "}
                      {t("easiDetails.nextOn", "The next one is on")} {formatDate(nextDate)}.
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="easid-take-btn"
                      onClick={() => { window.location.href = `/easi-score?${buildQs()}`; }}
                    >
                      {t("easiDetails.takeBtn", "TAKE AN EASI")}
                    </button>
                  )}
                </div>
              )}

              {scoreData && (
                <>
                  <label className="easid-label">{t("easiDetails.previousLabel", "Previous EASI")}</label>
                  <div className="easid-score-card">
                    <div style={{ flex: 1 }}>
                      <p className="easid-difficulty-text" style={{ color: difficulty.color || "var(--text-primary)" }}>
                        {difficulty.text}
                      </p>
                      <div className="easid-difficulty-bar" style={{ background: difficulty.color || "#ccc" }} />
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <p className="easid-score-value">{latestScore}</p>
                      <p className="easid-score-max">(Out of 72)</p>
                    </div>
                    {reportUrl ? (
                      <a
                        href={`https://goqii.com/app?fsn=244&fssn=0&fai=${encodeURIComponent(JSON.stringify({ url: reportUrl, title: "EASI-Report" }))}`}
                        className="easid-report-link"
                      >
                        {t("easiDetails.viewReport", "View Report")}
                      </a>
                    ) : null}
                  </div>

                  <div className="easid-view-all-wrap">
                    <a
                      href={`https://goqii.com/app?fsn=242&fssn=4&fai=${encodeURIComponent(JSON.stringify({ url: `https://lifestyle1.goqii.com/self-stats?uuid=${uuid}`, title: "Flareup" }))}`}
                      className="easid-view-all-link"
                    >
                      {t("easiDetails.viewAll", "View All Previous EASI")}
                    </a>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default EasiDermaDetails;
